/**
 * Admin V3 — Operations / Audit — Search / filter bar
 *
 * Layout (left → right):
 *   [Action type dropdown] [Operator ID input] [Precedence hint, conditional]
 *
 * NO CSV export (no backend endpoint).
 * NO entity_type filter (backend silently ignores it — admin_control_tower_v2.py:1346-1368).
 *
 * Backend filter precedence (single-axis if/elif): if operator_id is set,
 * action_type is ignored server-side. The UI shows BOTH controls visible and
 * displays a small italic hint line when both are set, explaining the backend
 * behavior. No control is disabled — operators retain full transparency.
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { operatorActionTypeMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

// 18 OperatorActionType enum values, snake_case, ordered to match
// operator_log.py:36-73 (booking → payment → payout → event → incident →
// driver → notification → system → sensitive view).
const ACTION_TYPE_VALUES = [
  'cancel_booking', 'redispatch', 'manual_status_change',
  'retry_payment', 'refund_payment',
  'trigger_payout', 'hold_payout', 'release_payout',
  'reprocess_event', 'purge_dead_letter',
  'acknowledge_incident', 'resolve_incident', 'escalate_incident', 'update_incident',
  'reassign_driver',
  'resend_notification',
  'refresh_read_models', 'force_sync',
  'view_sensitive_data',
];

// Empty value for the "all" option. Select requires a non-empty string,
// so we use a sentinel and translate at the boundary.
const ALL_SENTINEL = '__all__';

const OperationsAuditSearchBar = ({
  actionType,
  onActionTypeChange,
  operatorId,
  onOperatorIdChange,
  locale = 'fr',
}) => {
  const selectValue = actionType || ALL_SENTINEL;
  const handleSelect = (v) => onActionTypeChange(v === ALL_SENTINEL ? '' : v);

  const bothSet = Boolean(actionType) && Boolean(operatorId.trim());

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        {/* Action type dropdown */}
        <Select value={selectValue} onValueChange={handleSelect}>
          <SelectTrigger
            className="h-10 w-[260px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
            data-testid="adminv3-operations-audit-action-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SENTINEL}>
              {t('operations.audit.filter.allActions', locale)}
            </SelectItem>
            {ACTION_TYPE_VALUES.map((v) => (
              <SelectItem key={v} value={v}>
                {operatorActionTypeMeta[v]?.label || v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Operator ID input */}
        <div className="relative flex-1 max-w-md min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={operatorId}
            onChange={(e) => onOperatorIdChange(e.target.value)}
            placeholder={t('operations.audit.search.placeholder', locale)}
            data-testid="adminv3-operations-audit-operator-input"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />
          {operatorId && (
            <button
              type="button"
              onClick={() => onOperatorIdChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid="adminv3-operations-audit-operator-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Precedence hint — only when both filters are set */}
      {bothSet && (
        <p
          className="text-[11px] italic text-amber-700"
          data-testid="adminv3-operations-audit-precedence-hint"
        >
          {t('operations.audit.filter.precedenceHint', locale)}
        </p>
      )}
    </div>
  );
};

export default OperationsAuditSearchBar;
